import { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { trainingStatsEndpoints } from '../../../endpoints/trainingStatistics';
import type {
  ITrainingStatistics,
  TrainingStatsPeriodType,
} from '../../../models/trainingStatistics';
import dayjs from 'dayjs';

/**
 * Hook: gestisce fetch e ricalcolo statistiche allenamento per team.
 * Spec: LEOPART_ProgrammazioneCampo_Spec_v1.1.1.md Sez 21.
 */

export type PeriodPreset = '7d' | '30d' | '3m' | 'season' | 'custom';

interface DateRange {
  start: string;
  end: string;
}

function getPresetRange(preset: PeriodPreset): DateRange {
  const now = dayjs();
  switch (preset) {
    case '7d':
      return { start: now.subtract(7, 'day').toISOString(), end: now.toISOString() };
    case '30d':
      return { start: now.subtract(30, 'day').toISOString(), end: now.toISOString() };
    case '3m':
      return { start: now.subtract(3, 'month').toISOString(), end: now.toISOString() };
    case 'season': {
      const year = now.month() >= 6 ? now.year() : now.year() - 1;
      return {
        start: dayjs(`${year}-07-01`).toISOString(),
        end: dayjs(`${year + 1}-06-30`).toISOString(),
      };
    }
    default:
      return { start: now.subtract(30, 'day').toISOString(), end: now.toISOString() };
  }
}

function presetToPeriodType(preset: PeriodPreset): TrainingStatsPeriodType {
  if (preset === '7d') return 'weekly';
  if (preset === 'season') return 'seasonal';
  return 'monthly';
}

interface UseTrainingStatisticsOptions {
  teamId: string | undefined;
  initialPreset?: PeriodPreset;
}

export function useTrainingStatistics({ teamId, initialPreset = '30d' }: UseTrainingStatisticsOptions) {
  const queryClient = useQueryClient();
  const [preset, setPreset] = useState<PeriodPreset>(initialPreset);
  const [customRange, setCustomRange] = useState<DateRange>(getPresetRange('30d'));
  const [compareEnabled, setCompareEnabled] = useState(false);

  const currentRange = preset === 'custom' ? customRange : getPresetRange(preset);
  const periodType = presetToPeriodType(preset);

  const queryKey = ['training-statistics', teamId, periodType, currentRange.start, currentRange.end];

  const { data: stats, isLoading, error } = useQuery<ITrainingStatistics, Error>({
    queryKey,
    queryFn: () =>
      trainingStatsEndpoints.getTeamStats(
        teamId!,
        periodType,
        currentRange.start,
        currentRange.end,
      ),
    enabled: !!teamId,
    staleTime: 5 * 60 * 1000,
  });

  // Periodo precedente (stagione precedente o periodo equivalente arretrato)
  const previousRange = (() => {
    const startD = dayjs(currentRange.start);
    const endD = dayjs(currentRange.end);
    const diffDays = endD.diff(startD, 'day');
    if (preset === 'season') {
      return {
        start: startD.subtract(1, 'year').toISOString(),
        end: endD.subtract(1, 'year').toISOString(),
      };
    }
    return {
      start: startD.subtract(diffDays + 1, 'day').toISOString(),
      end: startD.subtract(1, 'day').toISOString(),
    };
  })();

  const { data: previousStats } = useQuery<ITrainingStatistics, Error>({
    queryKey: ['training-statistics-prev', teamId, periodType, previousRange.start, previousRange.end],
    queryFn: () =>
      trainingStatsEndpoints.getTeamStats(
        teamId!,
        periodType,
        previousRange.start,
        previousRange.end,
      ),
    enabled: !!teamId && compareEnabled,
    staleTime: 10 * 60 * 1000,
  });

  const { mutateAsync: recalculate, isPending: isRecalculating } = useMutation({
    mutationFn: () =>
      trainingStatsEndpoints.forceRecalculate(
        teamId!,
        periodType,
        currentRange.start,
        currentRange.end,
      ),
    onSuccess: (data) => {
      queryClient.setQueryData(queryKey, data);
    },
  });

  const changePreset = useCallback((newPreset: PeriodPreset) => {
    setPreset(newPreset);
  }, []);

  const changeCustomRange = useCallback((start: string, end: string) => {
    setCustomRange({ start, end });
    setPreset('custom');
  }, []);

  return {
    stats,
    previousStats,
    isLoading,
    error,
    preset,
    currentRange,
    previousRange,
    periodType,
    compareEnabled,
    setCompareEnabled,
    changePreset,
    changeCustomRange,
    recalculate,
    isRecalculating,
  };
}
